import { isSuccessCode } from '../utils/apiUtils';
import { callUIAPI } from "../utils/apiUtils"
import { API_GOOGLE_SEARCH, API_WEB_SUMMARY, API_WEB_TITLE, API_YOUTUBE_SEARCH, API_YOUTUBE_SUMMARY,
  API_YOUTUBE_TRANSCRIPT, METHOD_POST } from "../utils/apiUtilsConstants"
import { ENTITY_NAME_KNOWLEDGE_BASE } from "./dbConstants";
import { SCREEN_NAME_KNOWLEDGE_BASE_ARTICLE } from '../constants';

/**
 * Search TripAdvisor for locations, hotels, restaurants, or attractions
 * @param {string} query The search query
 * @param {string} category The category to search ('geos', 'hotels', 'restaurants', 'attractions')
 * @returns {Promise<{results: Array, total: number}>} The search results
 */
export const searchTripadvisor = async (query, category, page = 1, pageSize = 10) => {
  if (!query || typeof query !== 'string' || !query.trim()) {
    console.error('TripAdvisor search error: Invalid query parameter', query);
    throw new Error('Search query is required for TripAdvisor search');
  }

  if (!category || !['all', 'geos', 'hotels', 'restaurants', 'attractions'].includes(category)) {
    console.error('TripAdvisor search error: Invalid category parameter', category);
    throw new Error('Valid category is required for TripAdvisor search');
  }

  console.log(`Starting TripAdvisor search: query=${query}, category=${category}`);
  const startTime = performance.now();

  try {
    const response = await callUIAPI(
      'tripadvisor/search', 
      { query, category, page, pageSize, screenName: SCREEN_NAME_KNOWLEDGE_BASE_ARTICLE, entityName:ENTITY_NAME_KNOWLEDGE_BASE }, 
      METHOD_POST, 
      true
    );
    const endTime = performance.now();
    console.log(`TripAdvisor API call completed in ${(endTime - startTime).toFixed(2)}ms`);
    console.log(`Response status: ${response.status} ${response.statusText}`);
    
    if (!isSuccessCode(response.status)) {
      const errorText = response.data.message;
      console.error('TripAdvisor search API error:', {
        status: response?.status,
        statusText: response?.statusText,
        errorText
      });
      throw new Error(`Failed to search TripAdvisor: ${errorText || response.statusText}`);
    }

    const data = response.data;
    
    if (!data?.results) {
      console.error('Invalid TripAdvisor search response structure:', data);
      throw new Error('Received invalid response structure from TripAdvisor search');
    }
    
    console.log(`TripAdvisor search returned ${data.results.length} results`);
    console.log('Sample result:', data.results);
    
    return {
      results: data.results || [],
      total: data.total || (data.results ? data.results.length : 0)
    };
  }
  catch (error) {
    console.error('Error searching TripAdvisor:', error);
    throw error;
  }
};


export const searchGooglePlaces = async (query) => {
  if (!query || typeof query !== 'string' || !query.trim()) {
    console.error('Google Places search error: Invalid query parameter', query);
    throw new Error('Search query is required for Google Places search');
  }

  console.log(`Starting Google Places search: query=${query}`);

  try {
    const response = await callUIAPI(
      'google/places/search',
      { query },
      METHOD_POST,
      true
    );

    if (!isSuccessCode(response.status)) {
      const errorText = response.data.message;
      console.error('Google Places search API error:', {
        status: response?.status,
        statusText: response?.statusText,
        errorText
      });
      throw new Error(`Failed to search Google Places: ${errorText || response.statusText}`);
    }

    const data = response.data;

    if (!data?.results) {
      console.error('Invalid Google Places search response structure:', data);
      throw new Error('Received invalid response structure from Google Places search');
    }

    console.log(`Google Places search returned ${data.results.length} results`);

    return {
      results: data.results || [],
      total: data.total || (data.results ? data.results.length : 0)
    };
  } catch (error) {
    console.error('Error searching Google Places:', error);
    throw error;
  }
};

export const searchGoogle = async(searchTerm, pageSize, startIndex, screenName, entityName, recordId) => {
  return callUIAPI(API_GOOGLE_SEARCH, {searchTerm, pageSize, startIndex, screenName, entityName, recordId}, METHOD_POST, true);
}

export const searchYoutube = async(searchTerm, pageSize, pageToken) => {
  return callUIAPI(API_YOUTUBE_SEARCH, {searchTerm, pageSize, pageToken}, METHOD_POST, true);
}

export const getYoutubeTranscript = async(url, screenName, entityName, recordId, retryCount = 1) => {
  return callUIAPI(API_YOUTUBE_TRANSCRIPT, {url, screenName, entityName, recordId}, METHOD_POST, true)
  .then(response => response.data)
  .catch(error => {
    const errorMessage = error?.response?.data?.message || error?.message || 'Error generating transcript';
    if(retryCount < 3 && errorMessage.includes('Request failed with status code 504')) {
      console.log('ArticleDAO - getYoutubeTranscript - Transcript generation timedout. Retrying', retryCount);
      return getYoutubeTranscript(url, screenName, entityName, recordId, retryCount + 1)
    }
    throw error;
  });
}

export const getYoutubeSummary = async(url, screenName, entityName, recordId, retryCount = 1) => {
  return callUIAPI(API_YOUTUBE_SUMMARY, {url, screenName, entityName, recordId}, METHOD_POST, true)
  .then(response => response.data)
  .catch(error => {
    const errorMessage = error?.response?.data?.message || error?.message || 'Error generating summary';
    if(retryCount < 3 && errorMessage.includes('Request failed with status code 504')) {
      console.log('ArticleDAO - getYoutubeSummary - Summary generation timedout. Retrying', retryCount);
      return getYoutubeSummary(url, screenName, entityName, recordId, retryCount + 1)
    }
    throw error;
  });
}

export const getWebPageSummary = (url, screenName, entityName, recordId) => {
  return callUIAPI(API_WEB_SUMMARY, {url, screenName, entityName, recordId}, METHOD_POST, true)
  .then(response => response.data);
}

export const getWebPageTitle = (url) => {
  return callUIAPI(API_WEB_TITLE, {url}, METHOD_POST, true, {timeout: 8000})
  .then(response => response.data?.title ?? null)
  .catch(() => null);
}
